import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Button, Typography } from 'antd';
import { ReloadOutlined, BugOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(
      '🚨 DynamicForm Error Boundary caught an error:',
      error,
      errorInfo
    );

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to external service in production
    if (process.env.NODE_ENV === 'production') {
      // Here you would typically send to error reporting service
      console.error('Production error:', {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <Alert
            message={
              <div>
                <Title level={4} style={{ color: '#ff4d4f', marginBottom: 8 }}>
                  <BugOutlined style={{ marginRight: 8 }} />
                  Something went wrong
                </Title>
                <Text type="secondary">
                  An unexpected error occurred while rendering the form. Please
                  try refreshing or contact support if the problem persists.
                </Text>
              </div>
            }
            type="error"
            showIcon={false}
            action={
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={this.handleRetry}
                style={{ marginTop: 16 }}
              >
                Try Again
              </Button>
            }
          />

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ marginTop: 16, textAlign: 'left' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                Error Details (Development Only)
              </summary>
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  fontSize: '12px',
                  marginTop: '8px',
                }}
              >
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
